import { useHubList } from "hooks/hub-management/useHub";
import { useMyHubsList } from "hooks/user-profile/useUserProfile";
import React, { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";

const HubSelect = ({
  isDisabled,
  hubIdDefault,
  setValue,
  value,
  onBlur,
  fieldName,
  isMyHubOnly,
  addressLevel,
  addressLevelHub,
  provinceCode,
  districtCode,
  ...props
}) => {
  const { t } = useTranslation(["hub"]);

  const getHubFilterParams = useMemo(() => {
    const baseFilter = { "filter[record_status]": "O" };

    if (addressLevel) {
      baseFilter["is_new_address"] = addressLevel === 2 ? "N" : "O";
    }

    if (addressLevelHub) {
      baseFilter["filter[is_new_address]"] = addressLevelHub === 2 ? "N" : "O";
    }

    if (provinceCode) {
      baseFilter["filter[province_code]"] = provinceCode;
    }
    if (districtCode && addressLevel === 2) {
      baseFilter["filter[district_code]"] = districtCode;
    }

    return baseFilter;
  }, [addressLevel, provinceCode, districtCode, addressLevelHub]);

  const { data: hubs, refetch: refetchHubs } = useHubList({
    key: `hub-select-${fieldName}-${addressLevel}-${provinceCode}-${districtCode}`,
    enabled: !isMyHubOnly,
    params: {
      page: -1,
      sort: "-maker_date",
      columnFilter: getHubFilterParams,
      keywords: "",
      fields: "",
      expand: "",
    },
  });

  const myHubsParams = useMemo(
    () => ({
      is_new_address: addressLevel === 2 ? "N" : "O",
      "filter[is_new_address]": addressLevelHub === 2 ? "N" : "O",
      ...(provinceCode && { "filter[province_code]": provinceCode }),
      ...(districtCode &&
        addressLevel === 2 && { "filter[district_code]": districtCode }),
    }),
    [addressLevel, provinceCode, districtCode, addressLevelHub]
  );

  const { data: myHubs, refetch: refetchMyHubs } = useMyHubsList(
    {
      queryKey: `my-hub-select-${fieldName}-${JSON.stringify(myHubsParams)}`,
      enabled: isMyHubOnly, // Chỉ enable khi có addressLevel
      params: myHubsParams,
    },
    res => {}
  );

  // Force refetch mỗi khi addressLevel thay đổi
  useEffect(() => {
    if (isMyHubOnly && addressLevel) {
      refetchMyHubs();
    }
  }, [addressLevel, isMyHubOnly, refetchMyHubs]);

  useEffect(() => {
    if (!isMyHubOnly && addressLevel) {
      refetchHubs();
    }
  }, [addressLevel, isMyHubOnly, refetchHubs]);

  useEffect(() => {
    if (!isMyHubOnly && addressLevelHub) {
      refetchHubs();
    }
  }, [addressLevelHub, isMyHubOnly, refetchHubs]);

  const options = useMemo(() => {
    if (isMyHubOnly) {
      if (myHubs) {
        const { hub, childs_hub } = myHubs;
        let allHubs = [hub, ...(childs_hub || [])].filter(Boolean);

        return allHubs.map(h => ({
          value: h.id,
          label: h.name,
        }));
      }
      return [];
    } else {
      return (
        hubs?.items?.map(item => {
          return {
            value: item.id,
            label: item.name,
          };
        }) || []
      );
    }
  }, [hubs?.items, myHubs, isMyHubOnly]);

  const defaultValue = useMemo(() => {
    if (value) {
      return value;
    }
    const optionSelect = options?.find(o => o.value === hubIdDefault);
    if (optionSelect && fieldName) {
      setValue(fieldName, optionSelect);
    }
    return optionSelect;
  }, [options, hubIdDefault, value]);

  return (
    <Select
      {...props}
      value={defaultValue}
      placeholder={t("placeholder.select", {
        title: "Center/Hub",
      })}
      options={options}
      classNamePrefix="select2-selection"
      isDisabled={isDisabled}
      onBlur={onBlur}
    />
  );
};
export default HubSelect;
